// © Microsoft Corporation. All rights reserved.
/**
 * This file contains utility functions for detecting the environment in which the app is running.
 * NOTE: "chpltfm" is mainly used by channel app-type. Instead, "ocid" is used by partner app-types.
 * WARNING: Be cautious when adding new imports to this file, as it is used in many places throughout the app and can potentially bloat the bundle size.
 */
import { IsomorphicStorage, canUseDOM, getUserAgent } from "@msnews/isomorphic-util";
import { EdgeMobileOcids } from "../constants";
import { memoizeLite } from "@msnews/core-helper";
/**
 * List of ocids used in native apps.
 * NOTE: this is a copy from constants/Constants.ts file to avoid unexpected files size bloating.
 */
const NativeAppOcids_outlookMobile = "OnOOutlookMobile"; // For Outlook mobile
const NativeAppOcids_msnCopilot = "cp_msn_news"; // For MSN on Copilot
const NativeAppOcids_sapphire = "OnOStartApp"; // For Start app
const NativeAppOcids_freFullPage = "emmxFre"; // OCID EM uses to open FRE page
const NativeAppOcids_winWeatherApp = "weather-app-win"; // For Windows Weather App (UWP)
const NativeAppOcids_xiaomiApp = "xmweather-"; // For Xiaomi super app and MI browser
const NativeAppOcids_launcher = "launcherntp"; // For Microsoft Launcher
const NativeAppOcids_launcherInterests = "launcherInterests"; // For insterests page in Microsoft Launcher
const NativeAppOcids_swiftKey = "swiftKey"; // For SwiftKey
const NativeAppOcids_winMoneyApp = "finance-app-win"; // For Windows Money App (UWP)
const SuperAppOcid = "superappdhp";
const CMCOcid = "cp_msn_cmc";
const WeatherMobileAppOcid = "weather-app-mobile";
const WeatherSuperAppOcid = "weather-super-app";
const WeatherOppoMobileOcid = "oppowx";
const ocidKey = "_ocidIsomorphicKey";
const DeviceFormFactor_Phone = "phone";
const getOcid = () => {
    if (!IsomorphicStorage.get(ocidKey)) {
        IsomorphicStorage.set(ocidKey, getQSPByName("ocid").toLowerCase());
    }
    return IsomorphicStorage.get(ocidKey);
};
const setOcid = () => IsomorphicStorage.set(ocidKey, getQSPByName("ocid").toLowerCase());
// possible chpltfm values: outlook-ios, outlook-android, office-ios, union-ios, office-android
const platformFlagKey = "_platformFlagKey";
const hostAppNameKey = "_hostAppNameKey";
const clientTypeKey = "_clientTypeKey";
const getPlatformFlag = () => {
    if (!IsomorphicStorage.get(platformFlagKey)) {
        IsomorphicStorage.set(platformFlagKey, getQSPByName("chpltfm"));
    }
    return IsomorphicStorage.get(platformFlagKey);
};
const getHostAppName = () => {
    if (!IsomorphicStorage.get(hostAppNameKey)) {
        const platformFlag = getPlatformFlag();
        IsomorphicStorage.set(hostAppNameKey, platformFlag.toLowerCase().split("-")[0]);
    }
    return IsomorphicStorage.get(hostAppNameKey);
};
const getClientType = () => {
    if (!IsomorphicStorage.get(clientTypeKey)) {
        const platformFlag = getPlatformFlag();
        IsomorphicStorage.set(clientTypeKey, platformFlag.toLowerCase().split("-")[1]);
    }
    return IsomorphicStorage.get(clientTypeKey);
};
const setPlatformRelevant = () => {
    const platformFlag = getQSPByName("chpltfm");
    const [hostAppName, clientType] = platformFlag.toLowerCase().split("-");
    IsomorphicStorage.set(platformFlagKey, platformFlag);
    IsomorphicStorage.set(hostAppNameKey, hostAppName);
    IsomorphicStorage.set(clientTypeKey, clientType);
};
/**
 * Reset the ocid and platformFlag for UT.
 * NOTE: this is only used for UT.
 */
export function _resetQspForUT() {
    setOcid();
    setPlatformRelevant();
}
/**
 * Checks if the app is running in a super app environment.
 * @returns {boolean} True if the app is running in a super app environment, false otherwise.
 */
export function isSuperApp() {
    if (!canUseDOM() || isSwiftKey()) {
        return false;
    }
    const bridge = window.sapphireWebViewBridge;
    const webkit = window.webkit;
    const isAndroidSuperApp = bridge && bridge.send;
    const isIOSSuperApp = webkit && webkit.messageHandlers && webkit.messageHandlers.send && webkit.messageHandlers.send.postMessage;
    return !!(isAndroidSuperApp || isIOSSuperApp);
}
/**
 * Checks if the app is running in a copilot.microsoft.com environment.
 * @returns {boolean} True if the app is running in a copilot.microsoft.com environment, false otherwise.
 */
export function isCMC() {
    if (!canUseDOM()) {
        return false;
    }
    return getOcid() === CMCOcid;
}
export function isCmcGem() {
    if (!canUseDOM()) {
        return false;
    }
    try {
        const pathParts = location.pathname.split("/");
        const articleId = pathParts[pathParts.length - 1];
        if (articleId.startsWith("gm-") && isCMC()) {
            return true;
        }
        else {
            return false;
        }
    }
    catch (e) {
        return false;
    }
}
/**
 * Check whether the app is running inside the copilot environment.
 * This is done by checking for the _isCopilotBridge flag.
 * @returns True if running inside copilot environment.
 */
export function isCopilot() {
    return !!window._isCopilotBridge
        || isCopilotTest();
}
/**
 * Checks if the app is running inside the copilot test environment.
 * @returns True if running inside copilot test environment with mocked bridge.
 */
export function isCopilotTest() {
    return getQSPByName("MBApp") === "copilot"; // for mocked bridge env
}
/**
 * Checks if the app is running in a super app browser environment.
 * @returns {boolean} True if the app is running in a super app browser environment, false otherwise.
 */
export function isBrowserSuperApp() {
    if (!canUseDOM()) {
        return false;
    }
    const currentHostname = window.location?.hostname;
    return currentHostname === "superapp.msn.com";
}
/**
 * Checks if the app is running in local test environment.
 * @returns {boolean} True if the app is in local test environment.
 */
export function isLocalHost() {
    const currentHostname = window.location?.hostname;
    return currentHostname === "localhost.msn.com";
}
/**
 * Check whether the app is running inside the Outlook mobile environment.
 * This is done by checking for platformFlag or ocid.
 * @returns {boolean} True if running inside Outlook mobile environment, false otherwise.
 */
export function isOutlookMobile() {
    const isPlatformOnOutlook = getPlatformFlag() && (getHostAppName() === "outlook" && ["ios", "android", "ipados"].includes(getClientType()));
    const isOcidOnOutlook = getOcid() === NativeAppOcids_outlookMobile.toLowerCase();
    return isPlatformOnOutlook || isOcidOnOutlook;
}
/**
 * Check whether the app is running inside the Copilot environment.
 * This is done by checking for platformFlag or ocid.
 * @returns {boolean} True if running inside Copilot environment, false otherwise.
 */
export function isMsnCopilot() {
    return getOcid() === NativeAppOcids_msnCopilot.toLowerCase();
}
/**
 * Check whether we are running inside the office mobile environment.
 * This is done by checking for ocid
 * @returns True if running inside office mobile environment.
 */
export function isWinWeatherApp() {
    return getOcid() === NativeAppOcids_winWeatherApp.toLowerCase();
}
/**
 * Check whether we are running inside the weather mobile app environment.
 * This is done by checking for ocid
 * @returns True if running inside weather mobile app environment.
 */
export function isWeatherMobileApp() {
    return getOcid() == WeatherMobileAppOcid;
}
export function isWeatherSuperApp() {
    return getOcid() == WeatherSuperAppOcid;
}
export function isWeatherOppoMobile() {
    return getOcid() === WeatherOppoMobileOcid;
}
/**
 * Check whether we are running inside the windows money app.
 * This is done by checking for ocid
 * @returns True if running inside the windows money app.
 */
export function isWinFinanceApp() {
    return getOcid() === NativeAppOcids_winMoneyApp.toLowerCase();
}
/**
 * Check whether we are running inside the metaos environment.
 * Because MetaOS haven't offer any method to indicate whether we are running inside it, we use the chpltfm to check.
 * @returns True if running inside metaos environment.
 */
export function isMetaos() {
    return isOutlookMobile();
}
/**
 * MetoaOS 2nd webview is a webview inside metaos webview, which is used by Outlook mobile right now.
 * @returns True if running inside metaos 2nd webview.
 */
export function isMetaos2ndWebView() {
    return getQSPByName("is2ndWV") === "true";
}
export function isFreFullPage() {
    return getOcid() === NativeAppOcids_freFullPage.toLowerCase();
}
export function shouldOpenLinksInSameTab() {
    return isSwiftKey();
}
/**
 * Check whether we are running inside the sapphire environment.
 * This is done by checking for the ocid or the bridge methods which are only available if running from sapphire.
 * @returns True if running inside sapphire environment.
 */
export function isSapphire() {
    if (!canUseDOM() || isSwiftKey()) {
        return false;
    }
    return isSuperApp() || getOcid() === NativeAppOcids_sapphire.toLowerCase() || getOcid() === SuperAppOcid.toLowerCase();
}
export function isLegacyEdgeMobile() {
    return getOcid() === "mmx";
}
/**
 * check whether we are running inside the edge mobile environment.
 * This is done by checking for the chpltfm and sapphire bridge sdk.
 * @returns True if running inside edge mobile environment.
 */
export function isEdgeMobile() {
    if (__WEBPACK_IS_NODE__) {
        return getOcid() === EdgeMobileOcids.edgeMobile;
    }
    // NOTE: legacy edge=1 flag is used by the old edge mobile app. Will remove it once the old edge mobile app is deprecated.
    const legacyEdgeFlag = getQSPByName("edge");
    return ((getHostAppName() === "emmx" || legacyEdgeFlag === "1") && isSapphire()) || isLegacyEdgeMobile();
}
/**
 * check whether we are running inside the edge mobile IOS environment.
 * @returns True if running inside the edge mobile IOS environment.
 */
export function isEdgeMobileIOS() {
    const userAgent = window.navigator.userAgent;
    return isEdgeMobile() && (userAgent.includes("EdgiOS") || userAgent.includes("iPhone") || userAgent.includes("iPad"));
}
/**
 * check whether we are running inside the 3rd party mobile environment.
 * eg: vivo, oppo, huawei, xiaomi, etc.
 * This is done by checking for the chpltfm
 * @returns True if running inside 3rd party mobile environment.
 */
export function isThirdPartyMobile() {
    return getHostAppName() === "3rdp" || getOcid().startsWith(NativeAppOcids_xiaomiApp) || isSwiftKey();
}
export function isVivo() {
    return getOcid() === "vivo" && isThirdPartyMobile();
}
export function isLauncher() {
    return [NativeAppOcids_launcher.toLowerCase(), NativeAppOcids_launcherInterests.toLowerCase()].includes(getOcid()) && isSapphire();
}
export function isEmmxInterests() {
    return getOcid() === "emmxinterests";
}
export function isLauncherInterests() {
    return getOcid() === NativeAppOcids_launcherInterests.toLowerCase();
}
export function isSwiftKey() {
    return getOcid().toLowerCase() === NativeAppOcids_swiftKey.toLowerCase() || getHostAppName().toLowerCase() == "swiftkey";
}
export function isBingMobile() {
    if (!canUseDOM()) {
        return false;
    }
    if (window._clientSettings?.pagetype === "bingHomepageMobile") {
        return true;
    }
    else {
        const widgetSettings = document.getElementsByClassName("peregrine-widget-settings")[0]?.getAttribute("data-client-settings");
        if (widgetSettings) {
            const clientSettings = JSON.parse(widgetSettings);
            return clientSettings && clientSettings.pagetype && clientSettings.pagetype === "bingHomepageMobile";
        }
        return false;
    }
}
export function isOfficialMetaOsApp() {
    return window.location.href.indexOf("metaos=true") !== -1;
}
/**
 * Sapphire Webview UserAgent:
 * Android: "Mozilla/5.0 (Linux; Android 10; VOG-AL10 Build/HUAWEIVOG-AL10; ) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/126.0.6478.134 Mobile Safari/537.36 NewsSapphire/28.9.360000000"
 * iOS: "Mozilla/5.0 (iPhone; CPU iPhone OS 17_4 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Version/17.4 Mobile/15E148 Safari/605.1.15 NewsSapphire/1.0.410307001"
 */
/**
 * Determine if it is in the Search Sapphire(Bing Search App) client environment.
 * @returns
 */
export function isSearchSapphire() {
    if (!isSapphire()) {
        return false;
    }
    const userAgent = getUserAgent();
    return userAgent.indexOf("BingSapphire") > -1;
}
/**
 * Determine if it is in the News Sapphire(Microsoft Start App/MSN App) client environment.
 * @returns
 */
export function isNewsSapphire() {
    if (!isSapphire()) {
        return false;
    }
    const userAgent = getUserAgent();
    return userAgent.indexOf("NewsSapphire") > -1;
}
/**
 * @returns True if running inside the sapphire sdk but don't need to use the sapphire bridge to send request.
 */
export function shouldSendRequestViaSapphireBridge() {
    return isSapphire() && !isEmmxInterests() && !isLauncherInterests() && !isThirdPartyMobile() && !isEdgeMobile();
}
/**
 * Check whether we are running inside the native application environment.
 * Native application environment includes Sapphire and MetaOS(TeamsJS)
 * @returns True if running inside native application environment.
 */
export const isNativeApp = memoizeLite(() => {
    return isSuperApp() || isOutlookMobile() || isSapphire() ||
        isWinWeatherApp() || isWinFinanceApp() || isEdgeMobile() || isThirdPartyMobile() ||
        isLauncher() || isSwiftKey() || isOfficialMetaOsApp() || isCopilot();
});
/**
 * Returns the value of the query string parameter with the specified name.
 * NOTE: can't import from UrlUtility because it will bloat file size
 * @param {string} name - The name of the query string parameter to retrieve.
 * @returns {string} The value of the query string parameter, or an empty string if it doesn't exist.
 */
function getQSPByName(name) {
    try {
        const url = new URL(location.href);
        return url.searchParams.get(name) || "";
    }
    catch (e) {
        return "";
    }
}
//# sourceMappingURL=NativeAppUtility.js.map